// Module: ScreenshotBrick
// Role: Self-contained screenshot/image upload brick for flow engine.
//       Single image: presign -> XHR -> submit.
// Uses: hooks/useFileUpload, api/flow-client, shared/limits
// Used by: brick-registry.ts (kind='screenshot')
//
// Pattern: UgcPhotoGridScreen single-photo path.
// ADR-023: no platform literals.

import { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { PlatformContext } from '../../../providers/PlatformProvider';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useArtefactPresign, useSubmitCheckpoint } from '../../../api/flow-client';
import { MAX_UGC_PHOTO_SIZE_BYTES } from '../../../shared/limits';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { Ic } from '../../../components/atoms/Ic';
import { P, S, T } from '../../../styles/tokens';
import { flowStepLabel } from '../_labels';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

const SUCCESS_SOFT_BG = '#EFF7E9';
const COPY_RESET_MS = 1600;
const SEARCH_QUERY_VISIBLE_SLUGS = new Set(['search']);

function screenshotHeading(checkpoint: Checkpoint): string {
  if (checkpoint.slug === 'search') return 'Загрузи скриншот поиска';
  if (checkpoint.slug === 'cart') return 'Загрузи скриншот корзины';
  if (checkpoint.slug === 'order') return 'Загрузи скриншот заказа';
  if (checkpoint.slug === 'pickup_status') return 'Загрузи скриншот ПВЗ / распаковки';
  if (checkpoint.slug === 'receipt_check') return 'Загрузи чек покупки';
  if (checkpoint.slug === 'package_barcode') return 'Загрузи фото упаковки';
  if (checkpoint.slug === 'product_photo') return 'Загрузи фото товара';
  if (checkpoint.slug === 'review_publish') return 'Загрузи скриншот отзыва';
  if (checkpoint.slug === 'control_screenshot') return 'Загрузи контрольный скрин';
  if (checkpoint.kind === 'PUBLISH') return 'Загрузи скриншот публикации';
  return 'Загрузи скриншот';
}

async function copyToClipboard(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

export interface ScreenshotBrickProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

export function ScreenshotBrick({
  checkpoint,
  flowRun,
  checkpoints,
}: ScreenshotBrickProps) {
  const platform = useContext(PlatformContext);
  const canUpload = platform?.capabilities.fileUpload ?? false;

  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<number | null>(null);
  // Tracks the current object URL so we can revoke it (no leak) when it is
  // replaced, on error reset, or on unmount.
  const thumbUrlRef = useRef<string | null>(null);

  const upload = useFileUpload();
  const presign = useArtefactPresign(flowRun.slot_id);
  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const parentCount = checkpoints.filter(
    (c) => c.parent_checkpoint_id === null,
  ).length;
  const searchQuery = checkpoint.step_context?.search_query;
  const showSearchQuery = Boolean(
    searchQuery && SEARCH_QUERY_VISIBLE_SLUGS.has(checkpoint.slug),
  );

  useEffect(() => () => {
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
    if (thumbUrlRef.current !== null) URL.revokeObjectURL(thumbUrlRef.current);
  }, []);

  const handleCopySearchQuery = useCallback(async () => {
    const query = searchQuery;
    if (!query) return;
    try {
      await copyToClipboard(query);
      setCopied(true);
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => setCopied(false), COPY_RESET_MS);
    } catch {
      setHintMessage('Не удалось скопировать. Выдели текст вручную.');
    }
  }, [searchQuery]);

  const handlePickFile = useCallback(() => {
    if (!canUpload) {
      setHintMessage('Загрузка файлов недоступна на этой платформе.');
      return;
    }
    if (isDone || isBusy) return;
    fileInputRef.current?.click();
  }, [canUpload, isDone, isBusy]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (!file) return;

      // 5 MB size guard
      if (file.size > MAX_UGC_PHOTO_SIZE_BYTES) {
        setHintMessage('Файл больше 5 МБ. Уменьши и попробуй снова.');
        return;
      }

      setHintMessage(null);

      // Show the picked screenshot immediately — synchronous with the tap.
      // Previously the thumb was created only after upload finished, so the
      // zone showed the old empty placeholder during presign + upload + verify.
      const objectUrl = URL.createObjectURL(file);
      if (thumbUrlRef.current !== null) URL.revokeObjectURL(thumbUrlRef.current);
      thumbUrlRef.current = objectUrl;
      setThumbUrl(objectUrl);
      setIsBusy(true);

      try {
        const presignResult = await presign.mutateAsync({
          checkpoint_slug: checkpoint.slug,
          artefact_kind: 'screenshot',
          content_md5: '',
          file_size_bytes: file.size,
        });

        await upload.start({ url: presignResult.upload_url, file });

        // Submit checkpoint with real artefact_path (EC-3)
        await submitCheckpoint.mutateAsync({
          artefact_path: presignResult.artefact_path,
        });

        setIsDone(true);
      } catch (err: unknown) {
        // BUG-1196: surface real error instead of generic toast
        const status =
          err && typeof err === 'object' && 'status' in err
            ? (err as { status: number }).status
            : undefined;
        const msg =
          err instanceof Error ? err.message : 'Неизвестная ошибка';
        setHintMessage(
          status
            ? `Ошибка загрузки (${status}): ${msg}`
            : `Ошибка загрузки: ${msg}`,
        );
        // Reset preview so the user can retry from a clean state.
        if (thumbUrlRef.current !== null) {
          URL.revokeObjectURL(thumbUrlRef.current);
          thumbUrlRef.current = null;
        }
        setThumbUrl(null);
      } finally {
        setIsBusy(false);
      }
    },
    [checkpoint.slug, presign, upload, submitCheckpoint],
  );

  return (
    <Frame>
      <MiniHeader title={checkpoint.display_name ?? flowStepLabel(checkpoint.kind)} />
      <div className="px-4 pt-3 pb-2">
        <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        <div style={{ paddingTop: 4 }}>
          <div style={{ font: T.title3, color: S.label, lineHeight: 1.2 }}>
            {screenshotHeading(checkpoint)}
          </div>
          <div
            style={{
              font: T.footnote,
              color: S.secondaryLabel,
              marginTop: 4,
            }}
          >
            {checkpoint.instructions_text ??
              'Скриншот со статусом «На модерации»'}
          </div>
        </div>

        {showSearchQuery && searchQuery && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 12,
              background: `rgba(226,114,91,0.08)`,
              border: `1px solid rgba(226,114,91,0.2)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <div>
              <div style={{ font: T.caption, color: S.secondaryLabel }}>Что искать:</div>
              <div style={{ font: T.subhead, color: S.label, fontWeight: 600, marginTop: 2 }}>
                {searchQuery}
              </div>
            </div>
            <button
              type="button"
              onClick={handleCopySearchQuery}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 6,
                borderRadius: 8,
                color: P.terracotta,
                font: T.caption,
                fontWeight: 600,
              }}
              aria-label="Скопировать запрос"
              data-testid="copy-search-query"
            >
              {copied ? 'Скопировано!' : 'Копировать'}
            </button>
          </div>
        )}

        <InstructionCard p={12}>
          {(() => {
            const hasThumb = thumbUrl !== null;
            const interactive = canUpload && !isDone && !isBusy;
            const busyLabel = submitCheckpoint.isPending
              ? 'Проверяем…'
              : 'Загружаем…';
            return (
              <div
                style={{
                  borderRadius: 16,
                  border: `2px dashed ${isDone ? P.success : P.terracotta}`,
                  background: isDone ? SUCCESS_SOFT_BG : P.terraSoft,
                  padding: hasThumb ? 0 : '28px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                  cursor: interactive ? 'pointer' : 'default',
                  overflow: 'hidden',
                  minHeight: hasThumb ? 200 : undefined,
                  position: 'relative',
                }}
                onClick={interactive ? handlePickFile : undefined}
                role={interactive ? 'button' : undefined}
                aria-label={isDone ? 'Скриншот загружен' : 'Загрузить скриншот'}
                data-testid="screenshot-upload-zone"
              >
                {hasThumb ? (
                  <>
                    <img
                      src={thumbUrl}
                      alt="Загруженный скриншот"
                      style={{
                        width: '100%',
                        height: '100%',
                        maxHeight: 320,
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                    {isBusy && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(0,0,0,0.42)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                        }}
                        data-testid="screenshot-busy-overlay"
                      >
                        <div
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            border: '3px solid rgba(255,255,255,0.4)',
                            borderTopColor: '#fff',
                            animation: 'spin 0.8s linear infinite',
                          }}
                        />
                        <div
                          style={{ font: T.subhead, fontWeight: 600, color: '#fff' }}
                        >
                          {busyLabel}
                        </div>
                      </div>
                    )}
                    {isDone && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: P.success,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Ic name="check" size={18} color="#fff" />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <Ic name="camera" size={28} color={P.terracotta} />
                    <div
                      style={{ font: T.subhead, fontWeight: 600, color: S.label }}
                    >
                      Нажми, чтобы выбрать скриншот
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {upload.status === 'uploading' && upload.progress !== null && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: P.line,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.round((upload.progress ?? 0) * 100)}%`,
                    background: P.terracotta,
                    transition: 'width 0.2s linear',
                  }}
                />
              </div>
            </div>
          )}
        </InstructionCard>

        {!canUpload && (
          <HintBar>Загрузка файлов недоступна на этой платформе.</HintBar>
        )}

        {hintMessage && canUpload && <HintBar>{hintMessage}</HintBar>}
      </div>

      {canUpload && !isDone && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="screenshot-input"
          aria-hidden="true"
        />
      )}

      <WizardCtaBar
        label={isDone ? 'Готово' : 'Загрузить скриншот'}
        onClick={handlePickFile}
        disabled={isDone || isBusy || !canUpload}
      />
    </Frame>
  );
}
